import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import MasterLookup from '../../components/workspace/MasterLookup'
import { currencyChoices, loadOrganisationSettings } from '../../utils/organisationSettings'

const FALLBACK_PRIORITY = [
  ['low', 'Low'],
  ['medium', 'Medium'],
  ['high', 'High'],
  ['critical', 'Critical']
];

const choiceList = (rows, fallback, current) => {
  const list = (rows || []).filter((row) => row?.code && row?.label).length
    ? rows.filter((row) => row?.code && row?.label).map((row) => ({ code: row.code, label: row.label }))
    : fallback.map(([code, label]) => ({ code, label }));
  const code = String(current || '').trim();
  if (code && !list.some((row) => row.code === code)) {
    list.push({ code, label: code.replace(/_/g, ' ') });
  }
  return list;
};

const personMark = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

const PipelineTenderForm = ({ formData, formErrors, onChange, variant = 'default', masters, team = [], onFiles, documentTypes = [], documentType = '', onDocumentType }) => {
  const workspace = variant === 'workspace';
  const [tenderCurrencies, setTenderCurrencies] = useState(['INR', 'USD', 'EUR', 'GBP', 'CAD', 'AUD']);
  const [orgTimezone, setOrgTimezone] = useState('Asia/Kolkata');
  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const settings = data?.settings;
      if (settings?.tenderCurrencies?.length) setTenderCurrencies(settings.tenderCurrencies);
      if (settings?.timezone) setOrgTimezone(settings.timezone);
    });
  }, []);
  const setField = (field) => (event) => {
    const raw = event.target.value
    const value = event.target.type === 'number' ? (raw === '' ? '' : Number(raw)) : raw
    onChange((prev) => ({ ...prev, [field]: value }))
  }
  const setValue = (field) => (value) => {
    onChange((prev) => ({ ...prev, [field]: value }))
  }
  const selectedTeam = (formData.teamMemberIds || []).map(String)
  const toggleMember = (id) => {
    const key = String(id)
    onChange((prev) => {
      const current = (prev.teamMemberIds || []).map(String)
      return {
        ...prev,
        teamMemberIds: current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
      }
    })
  }

  return (
    <div className="pipeline-tender-form">
      <section className="pipeline-tender-form__section">
        <h2>Identity & scope</h2>
        <p className="pipeline-tender-form__section-copy">Core identifiers and the opportunity narrative.</p>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Reference *</Form.Label>
              <Form.Control
                type="text"
                value={formData.reference}
                onChange={setField('reference')}
                placeholder="Enter tender reference"
                isInvalid={!!formErrors.reference}
              />
              <Form.Control.Feedback type="invalid">{formErrors.reference}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={setField('title')}
                placeholder="Enter tender title"
                isInvalid={!!formErrors.title}
              />
              <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>{workspace ? 'Client *' : 'Organization *'}</Form.Label>
              {workspace ? (
                <MasterLookup
                  options={masters?.customers || []}
                  value={formData.organization}
                  onChange={setValue('organization')}
                  placeholder="Search customers"
                  disabled={!masters?.ready}
                  invalid={!!formErrors.organization}
                />
              ) : (
              <Form.Control
                type="text"
                value={formData.organization}
                onChange={setField('organization')}
                placeholder="Enter organization name"
                isInvalid={!!formErrors.organization}
              />
              )}
              <Form.Control.Feedback type="invalid">{formErrors.organization}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Location *</Form.Label>
              <Form.Control
                type="text"
                value={formData.location}
                onChange={setField('location')}
                placeholder={workspace ? 'City, corridor, or package' : 'Enter location'}
                isInvalid={!!formErrors.location}
              />
              <Form.Control.Feedback type="invalid">{formErrors.location}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.description}
                onChange={setField('description')}
                placeholder={workspace ? 'Scope, client, and what the firm is being asked to deliver' : 'Enter tender description'}
                isInvalid={!!formErrors.description}
              />
              <Form.Control.Feedback type="invalid">{formErrors.description}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </section>

      <section className="pipeline-tender-form__section">
        <h2>{workspace ? 'Value' : 'Commercial profile'}</h2>
        <p className="pipeline-tender-form__section-copy">{workspace ? 'Total project value and the expected consultancy fee are separate. Unknown or not disclosed stays blank and is not stored as zero.' : 'Value, currency, and submission deadline.'}</p>
        <Row className="g-3">
          {workspace ? (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Total project value</Form.Label>
                  <Form.Select value={formData.projectValueStatus || 'unknown'} onChange={setField('projectValueStatus')} aria-label="Project value disclosure">
                    <option value="stated">Stated</option>
                    <option value="unknown">Unknown</option>
                    <option value="not_disclosed">Not disclosed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Project amount</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" value={formData.projectValueStatus === 'stated' ? formData.projectValue : ''} onChange={setField('projectValue')} placeholder="Leave blank when unknown" disabled={formData.projectValueStatus !== 'stated'} isInvalid={!!formErrors.projectValue} />
                  <Form.Control.Feedback type="invalid">{formErrors.projectValue}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Expected consultancy fee</Form.Label>
                  <Form.Select value={formData.consultancyFeeStatus || 'unknown'} onChange={setField('consultancyFeeStatus')} aria-label="Consultancy fee disclosure">
                    <option value="stated">Stated</option>
                    <option value="unknown">Unknown</option>
                    <option value="not_disclosed">Not disclosed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Fee amount</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" value={formData.consultancyFeeStatus === 'stated' ? formData.consultancyFee : ''} onChange={setField('consultancyFee')} placeholder="Leave blank when unknown" disabled={formData.consultancyFeeStatus !== 'stated'} isInvalid={!!formErrors.consultancyFee} />
                  <Form.Control.Feedback type="invalid">{formErrors.consultancyFee}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </>
          ) : (
          <Col md={4}>
            <Form.Group>
              <Form.Label>Estimated value *</Form.Label>
              <Form.Control
                type="number"
                value={formData.estimatedValue}
                onChange={setField('estimatedValue')}
                placeholder="Enter estimated value"
                isInvalid={!!formErrors.estimatedValue}
              />
              <Form.Control.Feedback type="invalid">{formErrors.estimatedValue}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          )}
          <Col md={4}>
            <Form.Group>
              <Form.Label>Currency</Form.Label>
              <Form.Select value={formData.currency} onChange={setField('currency')}>
                {currencyChoices(tenderCurrencies, formData.currency).map((code) => <option key={code} value={code}>{code}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          {workspace ? (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Submission date *</Form.Label>
                  <Form.Control type="date" value={formData.submissionDate || formData.deadline || ''} onChange={setField('submissionDate')} isInvalid={!!formErrors.submissionDate} />
                  <Form.Control.Feedback type="invalid">{formErrors.submissionDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Submission time</Form.Label>
                  <Form.Control type="time" value={formData.submissionTime || ''} onChange={setField('submissionTime')} />
                  {formData.timeConfirmationNeeded && !formData.submissionTime ? <div className="form-text">Time was not recorded. Confirm it before treating any clock time as the cutoff.</div> : null}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Time zone</Form.Label>
                  <Form.Select value={formData.deadlineTimezone || orgTimezone} onChange={setField('deadlineTimezone')} aria-label="Submission time zone">
                    {[orgTimezone, 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'UTC', 'Europe/London', 'America/New_York', 'America/Toronto', 'Australia/Sydney']
                      .filter((zone, index, list) => zone && list.indexOf(zone) === index)
                      .map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Publication date</Form.Label>
                  <Form.Control type="date" value={formData.publicationDate || ''} onChange={setField('publicationDate')} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Pre-bid meeting</Form.Label>
                  <Form.Control type="datetime-local" value={formData.preBidMeetingAt || ''} onChange={setField('preBidMeetingAt')} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Query deadline</Form.Label>
                  <Form.Control type="datetime-local" value={formData.queryDeadline || ''} onChange={setField('queryDeadline')} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Technical opening</Form.Label>
                  <Form.Control type="datetime-local" value={formData.technicalOpeningAt || ''} onChange={setField('technicalOpeningAt')} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Financial opening</Form.Label>
                  <Form.Select value={formData.financialOpeningAnnounced || ''} onChange={setField('financialOpeningAnnounced')}>
                    <option value="">Not recorded</option>
                    <option value="not_announced">Not announced</option>
                    <option value="announced">Announced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.financialOpeningAnnounced === 'announced' ? (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Financial opening date</Form.Label>
                    <Form.Control type="datetime-local" value={formData.financialOpeningAt || ''} onChange={setField('financialOpeningAt')} />
                  </Form.Group>
                </Col>
              ) : null}
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Bid validity (days)</Form.Label>
                  <Form.Control type="number" min="1" value={formData.bidValidityDays ?? ''} onChange={setField('bidValidityDays')} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Validity counted from</Form.Label>
                  <Form.Select value={formData.bidValidityReference || ''} onChange={setField('bidValidityReference')}>
                    <option value="">Not recorded</option>
                    <option value="submission_deadline">Submission deadline</option>
                    <option value="technical_opening">Technical opening</option>
                    <option value="financial_opening">Financial opening</option>
                    <option value="stated_date">A stated date</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.bidValidityReference === 'stated_date' ? (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Validity reference date</Form.Label>
                    <Form.Control type="date" value={formData.bidValidityReferenceDate || ''} onChange={setField('bidValidityReferenceDate')} />
                  </Form.Group>
                </Col>
              ) : null}
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Validity expires</Form.Label>
                  <Form.Control plaintext readOnly value={formData.bidValidityExpiresAt ? new Date(formData.bidValidityExpiresAt).toLocaleString() : 'Calculated when the days and reference date are both known'} />
                </Form.Group>
              </Col>
            </>
          ) : (
          <Col md={4}>
            <Form.Group>
              <Form.Label>Deadline *</Form.Label>
              <Form.Control
                type="date"
                value={formData.deadline}
                onChange={setField('deadline')}
                isInvalid={!!formErrors.deadline}
              />
              <Form.Control.Feedback type="invalid">{formErrors.deadline}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          )}
        </Row>
      </section>

      <section className="pipeline-tender-form__section">
        <h2>Classification</h2>
        <p className="pipeline-tender-form__section-copy">
          {workspace ? 'Where the opportunity was found, who may work on it, and the first files.' : 'Tender type, therapeutic area, and source attribution.'}
        </p>
        {workspace ? (
          <div className="classify">
            <div className="classify__band">
              <div className="classify__band-head">
                <span className="classify__kicker">Found</span>
              </div>
              <div className="classify__grid">
                <Form.Group>
                  <Form.Label>Tender type *</Form.Label>
                  <MasterLookup
                    options={masters?.tenderTypes || []}
                    value={formData.tenderType}
                    onChange={setValue('tenderType')}
                    placeholder="Search tender types"
                    disabled={!masters?.ready}
                    invalid={!!formErrors.tenderType}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.tenderType}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Source *</Form.Label>
                  <MasterLookup
                    options={masters?.sources || []}
                    value={formData.source}
                    onChange={setValue('source')}
                    placeholder="Search sources"
                    disabled={!masters?.ready}
                    invalid={!!formErrors.source}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.source}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="classify__span">
                  <Form.Label>Tender portal URL</Form.Label>
                  <Form.Control type="url" value={formData.sourceUrl || ''} onChange={setField('sourceUrl')} placeholder="https://" />
                </Form.Group>
              </div>
            </div>

            <div className="classify__band">
              <div className="classify__band-head">
                <span className="classify__kicker">People</span>
                <p className="classify__note">{selectedTeam.length ? `${selectedTeam.length} added with the owner` : 'The owner is included'}</p>
              </div>
              <Form.Group>
                <Form.Label>Tender owner</Form.Label>
                <Form.Select value={formData.ownerId || ''} onChange={setField('ownerId')} aria-label="Tender owner">
                  {(team || []).map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </Form.Select>
                <div className="form-text">The creator stays the owner when this is left as the signed-in person.</div>
              </Form.Group>
              <div className="classify__chips" role="group" aria-label="Authorised team members">
                {(team || []).filter((person) => String(person.id) === String(formData.ownerId)).map((person) => (
                  <span key={person.id} className="classify__person is-owner">
                    <span className="classify__mark" aria-hidden>{personMark(person.name)}</span>
                    {person.name}
                    <span className="classify__role">Owner</span>
                  </span>
                ))}
                {(team || []).filter((person) => String(person.id) !== String(formData.ownerId)).map((person) => {
                  const on = selectedTeam.includes(String(person.id))
                  return (
                    <button
                      key={person.id}
                      type="button"
                      className={on ? 'classify__person is-on' : 'classify__person'}
                      aria-pressed={on}
                      onClick={() => toggleMember(person.id)}
                    >
                      <span className="classify__mark" aria-hidden>{personMark(person.name)}</span>
                      {person.name}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="classify__band">
              <div className="classify__band-head">
                <span className="classify__kicker">Scope</span>
              </div>
              <div className="classify__grid">
                <Form.Group>
                  <Form.Label>Service</Form.Label>
                  <MasterLookup
                    options={masters?.services || []}
                    value={formData.serviceType}
                    onChange={setValue('serviceType')}
                    placeholder="Search services"
                    disabled={!masters?.ready}
                    invalid={!!formErrors.serviceType}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.serviceType}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Sector</Form.Label>
                  <MasterLookup
                    options={masters?.sectors || []}
                    value={formData.sector}
                    onChange={setValue('sector')}
                    placeholder="Search sectors"
                    disabled={!masters?.ready}
                    invalid={!!formErrors.sector}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.sector}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Participation</Form.Label>
                  <Form.Select value={formData.participationMode || ''} onChange={setField('participationMode')}>
                    <option value="">Not recorded</option>
                    <option value="undecided">Undecided</option>
                    <option value="solo">Solo</option>
                    <option value="jv_lead">JV lead</option>
                  </Form.Select>
                </Form.Group>
                {formData.participationApprovedMode && formData.participationMode && formData.participationMode !== formData.participationApprovedMode ? (
                  <Form.Group>
                    <Form.Label>Reason for changing participation</Form.Label>
                    <Form.Control value={formData.participationChangeReason || ''} onChange={setField('participationChangeReason')} />
                  </Form.Group>
                ) : null}
              </div>
            </div>

            {onFiles ? (
              <div className="classify__band">
                <div className="classify__band-head">
                  <span className="classify__kicker">Files</span>
                  <p className="classify__note">Optional. The tender is saved first.</p>
                </div>
                <div className="classify__grid">
                  {documentTypes.length ? (
                    <Form.Group>
                      <Form.Label>Document type</Form.Label>
                      <Form.Select aria-label="Document type" value={documentType} onChange={(event) => onDocumentType?.(event.target.value)}>
                        {documentTypes.map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  ) : null}
                  <Form.Group className={documentTypes.length ? undefined : 'classify__span'}>
                    <Form.Label>Upload</Form.Label>
                    <Form.Control type="file" multiple onChange={(event) => onFiles(Array.from(event.target.files || []))} />
                  </Form.Group>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Tender type *</Form.Label>
              <Form.Select
                value={formData.tenderType}
                onChange={setField('tenderType')}
                isInvalid={!!formErrors.tenderType}
              >
                <option value="">Select tender type</option>
                <option value="Public Procurement">Public Procurement</option>
                <option value="Hospital Tender">Hospital Tender</option>
                <option value="Government RFP">Government RFP</option>
                <option value="Private Tender">Private Tender</option>
                <option value="Framework Agreement">Framework Agreement</option>
                <option value="Supply Agreement">Supply Agreement</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{formErrors.tenderType}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Therapeutic area *</Form.Label>
              <Form.Select
                value={formData.therapeuticArea}
                onChange={setField('therapeuticArea')}
                isInvalid={!!formErrors.therapeuticArea}
              >
                <option value="">Select therapeutic area</option>
                <option value="Diabetes">Diabetes</option>
                <option value="Rare Diseases">Rare Diseases</option>
                <option value="Cardiovascular">Cardiovascular</option>
                <option value="Oncology">Oncology</option>
                <option value="Neurology">Neurology</option>
                <option value="Respiratory">Respiratory</option>
                <option value="Other">Other</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{formErrors.therapeuticArea}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Source *</Form.Label>
              <Form.Control
                type="text"
                value={formData.source}
                onChange={setField('source')}
                placeholder="Enter tender source"
                isInvalid={!!formErrors.source}
              />
              <Form.Control.Feedback type="invalid">{formErrors.source}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        )}
      </section>

      <section className="pipeline-tender-form__section">
        <h2>{workspace ? 'Tender stage' : 'Pursuit governance'}</h2>
        <p className="pipeline-tender-form__section-copy">{workspace ? 'Priority and urgency stay on this form. The journey and the outcome are changed from the tender workspace.' : 'Stage, priority, urgency, and win probability.'}</p>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>{workspace ? 'Journey' : 'Pipeline stage'}</Form.Label>
              {workspace ? (
                <>
                  <Form.Control plaintext readOnly value={formData.lifecycle?.positionLabel || ({ identified: 'Identified' }[formData.workflowStage] || formData.workflowStage || 'Identified')} />
                  {formData.lifecycle?.reviewNote ? <div className="form-text">{formData.lifecycle.reviewNote}</div> : null}
                </>
              ) : (
                <Form.Select value={formData.pipelineStage} onChange={setField('pipelineStage')}>
                  <option value="identified">Identified</option>
                  <option value="evaluating">Evaluating</option>
                  <option value="pursuing">Pursuing</option>
                  <option value="submitted">Submitted</option>
                  <option value="awarded">Awarded</option>
                  <option value="lost">Lost</option>
                </Form.Select>
              )}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Priority</Form.Label>
              <Form.Select value={formData.priority} onChange={setField('priority')}>
                {choiceList(masters?.priorities, FALLBACK_PRIORITY, formData.priority).map((row) => (
                  <option key={row.code} value={row.code}>{row.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Urgency</Form.Label>
              <Form.Select value={formData.urgency} onChange={setField('urgency')}>
                {choiceList(masters?.urgencies, FALLBACK_PRIORITY, formData.urgency).map((row) => (
                  <option key={row.code} value={row.code}>{row.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>{workspace ? 'Win probability (%)' : 'Win probability (%)'}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={formData.winProbability}
                onChange={setField('winProbability')}
                placeholder={workspace ? 'Optional' : undefined}
              />
            </Form.Group>
          </Col>
        </Row>
        {workspace && formData.lifecycle?.outcomeLabel ? (
          <p className="form-text mt-2">Outcome: {formData.lifecycle.outcomeLabel}{formData.outcomeReasonLabel ? ` · ${formData.outcomeReasonLabel}` : ''}</p>
        ) : null}
      </section>
    </div>
  )
}

export default PipelineTenderForm
